/* eslint-disable max-depth */
import axios, { type Method, type RawAxiosRequestHeaders } from "axios";
import { isBrowser } from "browser-or-node";

import type { DropNotificationParams, OrdersScoringParams } from "../types/index.js";

export const GET = "GET";
export const POST = "POST";
export const DELETE = "DELETE";
export const PUT = "PUT";

const overloadHeaders = (
	method: Method,
	headers: Record<string, string | number | boolean> = {},
) => {
	if (isBrowser) {
		return;
	}

	headers["User-Agent"] = `@polymarket/clob-client`;
	headers.Accept = "*/*";
	headers.Connection = "keep-alive";
	headers["Content-Type"] = "application/json";

	if (method === GET) {
		headers["Accept-Encoding"] = "gzip";
	}
};

export const request = async (
	endpoint: string,
	method: Method,
	headers?: any,
	data?: any,
	params?: any,
): Promise<any> => {
	overloadHeaders(method, headers);
	return await axios({ method, url: endpoint, headers, data, params });
};

export type QueryParams = Record<string, any>;

export interface RequestOptions {
	headers?: RawAxiosRequestHeaders;
	data?: any;
	params?: QueryParams;
}

const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

export const post = async (
	endpoint: string,
	options?: RequestOptions,
	retryOnError: boolean = false,
): Promise<any> => {
	try {
		const resp = await request(
			endpoint,
			POST,
			options?.headers,
			options?.data,
			options?.params,
		);
		return resp.data;
	} catch (err: unknown) {
		if (retryOnError && isTransientAxiosError(err)) {
			await sleep(30);
			try {
				const resp = await request(
					endpoint,
					POST,
					options?.headers,
					options?.data,
					options?.params,
				);
				return resp.data;
			} catch (retryErr: unknown) {
				return errorHandling(retryErr);
			}
		}
		return errorHandling(err);
	}
};

export const get = async (endpoint: string, options?: RequestOptions): Promise<any> => {
	try {
		const resp = await request(endpoint, GET, options?.headers, options?.data, options?.params);
		return resp.data;
	} catch (err: unknown) {
		return errorHandling(err);
	}
};

export const del = async (endpoint: string, options?: RequestOptions): Promise<any> => {
	try {
		const resp = await request(
			endpoint,
			DELETE,
			options?.headers,
			options?.data,
			options?.params,
		);
		return resp.data;
	} catch (err: unknown) {
		return errorHandling(err);
	}
};

const errorHandling = (err: unknown) => {
	if (axios.isAxiosError(err)) {
		if (err.response) {
			if (err.response?.data) {
				if (
					typeof err.response?.data === "string" ||
					err.response?.data instanceof String
				) {
					return { error: err.response?.data, status: err.response?.status };
				}
				// biome-ignore lint/suspicious/noPrototypeBuiltins: ES2022 Object.hasOwn not available with current target
				if (!Object.prototype.hasOwnProperty.call(err.response?.data, "error")) {
					return { error: err.response?.data, status: err.response?.status };
				}
				// in this case the field 'error' is included
				return { ...err.response?.data, status: err.response?.status };
			}
		}

		if (err.message) {
			return { error: err.message };
		}
	}

	return { error: err };
};

const isTransientAxiosError = (err: unknown): boolean => {
	if (!axios.isAxiosError(err)) return false;
	if (!err.response) return true; // network error
	const status = err.response.status ?? 0;
	if (status >= 500 && status < 600) return true; // 5xx
	const code = (err.code ?? "").toString();
	return ["ECONNABORTED", "ENETUNREACH", "EAI_AGAIN", "ETIMEDOUT"].includes(code);
};

export const parseOrdersScoringParams = (orderScoringParams?: OrdersScoringParams): QueryParams => {
	const params: QueryParams = {};
	if (orderScoringParams !== undefined) {
		if (orderScoringParams.orderIds !== undefined) {
			params.order_ids = orderScoringParams?.orderIds.join(",");
		}
	}
	return params;
};

export const parseDropNotificationParams = (
	dropNotificationParams?: DropNotificationParams,
): QueryParams => {
	const params: QueryParams = {};
	if (dropNotificationParams !== undefined) {
		if (dropNotificationParams.ids !== undefined) {
			params.ids = dropNotificationParams?.ids.join(",");
		}
	}
	return params;
};
